import * as THREE from 'three';
import { Enemy } from './Enemy';
import { EnemyType } from './EnemyType';
import { EnemyArrow } from './EnemyArrow';

/**
 * Archer enemy - ranged attacker that shoots arrows
 */
export class Archer extends Enemy {
  private bow: THREE.Group = new THREE.Group(); // Initialize with empty group
  private arrows: EnemyArrow[] = [];
  private maxArrows: number = 5; // Maximum number of active arrows
  private attackAnimationProgress: number = 0;
  private drawingBow: boolean = false;
  
  /**
   * Creates a new Archer enemy
   * @param position The initial position
   * @param scene The scene to add the enemy to
   */
  constructor(position: THREE.Vector3, scene: THREE.Scene) {
    super(EnemyType.ARCHER, position, scene);
    
    // Add archer-specific details to the model
    this.addModelDetails();
  }
  
  /**
   * Adds visual details to the archer model
   */
  private addModelDetails(): void {
    // Add eyes
    const eyeGeometry = new THREE.SphereGeometry(0.08, 8, 8);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(0.15, 0.2, 0.3);
    this.model.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(-0.15, 0.2, 0.3);
    this.model.add(rightEye);
    
    // Add pupils
    const pupilGeometry = new THREE.SphereGeometry(0.04, 8, 8);
    const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    
    const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
    leftPupil.position.set(0, 0, 0.04);
    leftEye.add(leftPupil);
    
    const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
    rightPupil.position.set(0, 0, 0.04);
    rightEye.add(rightPupil);
    
    // Create bow
    this.bow = this.createBow();
    this.bow.position.set(0.4, 0, 0.2);
    this.model.add(this.bow);
    
    // Add quiver with arrows
    const quiverGeometry = new THREE.CylinderGeometry(0.1, 0.1, 0.5, 8);
    const quiverMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const quiver = new THREE.Mesh(quiverGeometry, quiverMaterial);
    quiver.position.set(-0.3, 0, -0.2);
    quiver.rotation.x = Math.PI / 6;
    this.model.add(quiver);
    
    // Add arrows in quiver
    for (let i = 0; i < 3; i++) {
      const arrowGeometry = new THREE.CylinderGeometry(0.01, 0.01, 0.4, 8);
      const arrowMaterial = new THREE.MeshStandardMaterial({ color: 0xCCCCCC });
      const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
      arrow.position.set(0.03 * i - 0.03, 0.2, 0);
      arrow.rotation.x = Math.PI / 2;
      quiver.add(arrow);
      
      // Add arrowhead
      const arrowheadGeometry = new THREE.ConeGeometry(0.02, 0.05, 8);
      const arrowheadMaterial = new THREE.MeshStandardMaterial({ color: 0x888888 });
      const arrowhead = new THREE.Mesh(arrowheadGeometry, arrowheadMaterial);
      arrowhead.position.y = 0.225;
      arrow.add(arrowhead);
    }
  }
  
  /**
   * Creates a bow model
   * @returns The bow group
   */
  private createBow(): THREE.Group {
    const bowGroup = new THREE.Group();
    
    // Bow body
    const bowGeometry = new THREE.TorusGeometry(0.2, 0.01, 8, 16, Math.PI);
    const bowMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const bowBody = new THREE.Mesh(bowGeometry, bowMaterial);
    bowBody.rotation.y = Math.PI / 2;
    bowGroup.add(bowBody);
    
    // Bow string
    const stringGeometry = new THREE.BufferGeometry();
    const stringMaterial = new THREE.LineBasicMaterial({ color: 0xFFFFFF });
    const stringPoints = [
      new THREE.Vector3(0, 0.2, 0),
      new THREE.Vector3(0, -0.2, 0)
    ];
    stringGeometry.setFromPoints(stringPoints);
    const bowString = new THREE.Line(stringGeometry, stringMaterial);
    bowGroup.add(bowString);
    
    return bowGroup;
  }
  
  /**
   * Makes the archer attack
   */
  public attack(): void {
    if (this.isDead || !this.isActive) return;
    
    const currentTime = performance.now() / 1000;
    if (currentTime - this.lastAttackTime < this.attackCooldown) {
      return; // Still on cooldown
    }
    
    super.attack();
    
    // Start drawing bow
    this.drawingBow = true;
    this.attackAnimationProgress = 0;
  }
  
  /**
   * Updates the archer
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The current player position
   */
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    super.update(deltaTime, playerPosition);
    
    // Update attack animation
    if (this.isAttacking) {
      this.updateAttackAnimation(deltaTime, playerPosition);
    }
    
    // Update arrows
    this.updateArrows(deltaTime);
    
    // Clean up inactive arrows
    this.cleanupArrows();
  }
  
  /**
   * Updates the attack animation
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The player's position
   */
  private updateAttackAnimation(deltaTime: number, playerPosition: THREE.Vector3): void {
    // Progress the attack animation
    this.attackAnimationProgress += deltaTime;
    
    // Normalize progress to 0-1 range
    const normalizedProgress = Math.min(this.attackAnimationProgress / 1.0, 1.0);
    
    // Face the player
    this.faceTarget(playerPosition);
    
    if (this.drawingBow) {
      // Drawing bow phase
      if (normalizedProgress < 0.7) {
        // Draw the bow
        this.updateBowString(normalizedProgress / 0.7);
      } else {
        // Release the arrow
        this.fireArrow(playerPosition);
        this.drawingBow = false;
        
        // Reset bow string
        this.updateBowString(0);
      }
    }
  }
  
  /**
   * Updates the bow string during drawing
   * @param drawProgress The draw progress (0-1)
   */
  private updateBowString(drawProgress: number): void {
    // Update bow string geometry
    const stringPoints = [
      new THREE.Vector3(0, 0.2, 0),
      new THREE.Vector3(-drawProgress * 0.1, 0, 0), // Middle point pulls back
      new THREE.Vector3(0, -0.2, 0)
    ];
    
    const stringGeometry = new THREE.BufferGeometry().setFromPoints(stringPoints);
    
    // Replace the bow string
    if (this.bow.children[1] instanceof THREE.Line) {
      const oldString = this.bow.children[1];
      if (oldString.geometry) {
        oldString.geometry.dispose();
      }
      
      const stringMaterial = (oldString.material as THREE.LineBasicMaterial).clone();
      const newString = new THREE.Line(stringGeometry, stringMaterial);
      
      this.bow.remove(oldString);
      this.bow.add(newString);
    }
  }
  
  /**
   * Fires an arrow at the target
   * @param targetPosition The target position
   */
  private fireArrow(targetPosition: THREE.Vector3): void {
    // Check if we've reached the maximum number of active arrows
    if (this.arrows.length >= this.maxArrows) {
      return;
    }
    
    // Calculate arrow direction
    const direction = new THREE.Vector3().subVectors(targetPosition, this.position).normalize();
    
    // Add some randomness to the direction
    direction.x += (Math.random() - 0.5) * 0.1;
    direction.y += (Math.random() - 0.5) * 0.1;
    direction.z += (Math.random() - 0.5) * 0.1;
    direction.normalize();
    
    // Create arrow starting position (at the bow)
    const arrowPosition = new THREE.Vector3().copy(this.position);
    arrowPosition.y += 1.0; // Adjust to archer's height
    
    // Add offset in the direction the archer is facing
    const facingDirection = new THREE.Vector3(0, 0, 1).applyQuaternion(this.object.quaternion);
    arrowPosition.add(facingDirection.multiplyScalar(0.5));
    
    // Create and fire the arrow
    const arrow = new EnemyArrow(arrowPosition, direction, this.damage, this.scene);
    this.arrows.push(arrow);
    
    console.log('Archer fired an arrow!');
  }
  
  /**
   * Updates all active arrows
   * @param deltaTime Time since last update in seconds
   */
  private updateArrows(deltaTime: number): void {
    for (const arrow of this.arrows) {
      arrow.update(deltaTime);
    }
  }
  
  /**
   * Cleans up inactive arrows
   */
  private cleanupArrows(): void {
    this.arrows = this.arrows.filter(arrow => arrow.isActive());
  }
  
  /**
   * Makes the archer face a target
   * @param targetPosition The target position
   */
  private faceTarget(targetPosition: THREE.Vector3): void {
    // Calculate direction to target
    const direction = new THREE.Vector3().subVectors(targetPosition, this.position).normalize();
    
    // Calculate rotation to face target
    const targetRotation = Math.atan2(direction.x, direction.z);
    
    // Set rotation
    this.object.rotation.y = targetRotation;
  }
  
  /**
   * Handles archer death
   */
  protected die(): void {
    super.die();
    
    // Play death animation - fall over
    this.model.rotation.x = Math.PI / 2; // Fall forward
    this.model.position.z = this.getHeight() / 2; // Move forward as it falls
    this.model.position.y = 0; // Lie on the ground
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    super.dispose();
    
    // Dispose of arrows
    for (const arrow of this.arrows) {
      arrow.dispose();
    }
    this.arrows = [];
    
    // Dispose of bow
    this.bow.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
} 